import { EBrowserStorageType } from '@/core/apis/browser_storage_api/browser_storage_api_types';
import { TMarketConfigData } from '@/core/apis/config_api/config_api_types';
import { TMarketIdentifiers } from '@/core/constants/event_types/base_mable_event';
import { Logger } from '@/shared/logger/logger';

import { StorageHandler } from '../storage_handler';

type TUpdateMarketDataResult = {
  isUpdated: boolean;
  mktData?: TMarketIdentifiers;
};
export class MarketDataHandler {
  /**
   * List of market data for the current shop
   */
  private static shopMarketData: TMarketConfigData[];

  /**
   * default market ID
   */
  static primaryMarketID: string;

  /**
   * Get the default market ID
   * @returns The default market ID
   */
  static setPrimaryMarketID() {
    const primaryMarket = this.shopMarketData.find(mkt => mkt.is_primary);
    if (!primaryMarket) {
      // If no default market is found, return an empty string
      Logger.error('No primary market found', {
        shopMarketData: this.shopMarketData,
      });
      this.primaryMarketID = '';
      return;
    }
    this.primaryMarketID = primaryMarket.mid;
  }

  /**
   * Set the shop market data
   * @param mktData - Market Data
   */
  static setShopMarketData(mktData: TMarketConfigData[]) {
    this.shopMarketData = mktData;
    this.setPrimaryMarketID(); // set the primary market id
  }

  private static readonly storageHandler =
    new StorageHandler<TMarketIdentifiers>(
      'wtag_mkt_dt',
      { do: '', lo: '', mid: '', re: '' },
      EBrowserStorageType.LOCALSTORAGE,
    );

  static getMarketData(): TMarketIdentifiers {
    return this.storageHandler.get();
  }

  static setMarketData(mktData: TMarketIdentifiers): void {
    this.storageHandler.set(mktData);
  }

  /**
   * Updates market data by comparing and merging current and provided market identifiers
   *
   * @param currentData - Existing market identifiers
   * @param mktData - New market identifiers to be merged
   * @returns {TUpdateMarketDataResult} Object containing:
   *   - isUpdated: true if any values were modified
   *   - mktData: resulting market data after merge
   *
   * Logic flow:
   * 1. If no currentData exists:
   *    - Returns {isUpdated: false} if mktData is also missing
   *    - Returns {isUpdated: false, mktData} if only mktData exists
   *
   * 2. If only currentData exists:
   *    - Uses currentData as mktData
   *    - Returns {isUpdated: true, mktData}
   *
   * 3. When both exist:
   *    - Compares and updates mh, lo, re, do fields
   *    - Updates is_captured flag if any changes occurred
   *    - Returns merged result with update status
   */
  static updateProvidedMarketData(
    currentData?: TMarketIdentifiers,
    mktData?: TMarketIdentifiers,
  ): TUpdateMarketDataResult {
    let isUpdated = false;
    // Handle cases where either or both inputs are missing
    if (!currentData) {
      return {
        isUpdated,
        mktData: mktData || undefined,
      };
    }

    // if no market data is provided, we'll use the current data
    if (!mktData) {
      return {
        isUpdated: true,
        mktData: { ...currentData, is_captured: false },
      };
    }

    // Compare and update market identifiers
    const identifiers = ['mh', 'lo', 're', 'do', 'mid'] as const;

    // Check each identifier for changes
    for (const id of identifiers) {
      const val = currentData[id];
      if (val && val !== mktData[id]) {
        mktData[id] = val;
        isUpdated = true;
      }
    }

    // Update capture flag if any changes were made
    if (isUpdated) {
      mktData.is_captured = currentData.is_captured;
    }

    return { isUpdated, mktData };
  }

  /**
   * Update the market data
   * @param mktData - Market Data Identifier
   */
  static updateMarketData(mktData: TMarketIdentifiers): void {
    const currentData = this.storageHandler.get();
    const updatedData: TMarketIdentifiers = {
      ...currentData,
      ...mktData,
    };
    this.storageHandler.set(updatedData);
  }

  /**
   * Get the market data hash
   * @param mktData - Market Data Identifier
   * @returns - Market Data Hash
   */
  static getMarketDataHash(mktData: TMarketIdentifiers) {
    return mktData.do + mktData.mid + mktData.re + mktData.lo;
  }

  /**
   * Finds the best matching market data based on strict priority rules
   * @param mktData - Market identifiers to match against
   * @returns The best matching market data, or undefined if no match is found
   *
   * Priority Order (highest to lowest):
   * 1. Market Handle (mh) - Exact match, immediate return
   * 2. Domain (do) - Exact match, immediate return
   * 3. Region (re) - If single match found, return. If multiple, check locale
   * 4. Locale (lo) - Final fallback for multiple region matches
   */
  static findMarketMatch(
    mktData: TMarketIdentifiers,
  ): TMarketConfigData | undefined {
    if (mktData.mid) {
      const handleMatch = this.shopMarketData.find(
        mkt => mkt.mid === mktData.mid,
      );
      if (handleMatch) return handleMatch;
    }
    // Priority 1: Handle match (highest priority)
    if (mktData.mh) {
      const handleMatch = this.shopMarketData.find(
        mkt => mkt.mh === mktData.mh,
      );
      if (handleMatch) return handleMatch;
    }

    // TODO: Add it later Priority 2: Domain match
    // if (mktData.do) {
    //   // Partial match for domain
    //   const domainMatch = this.shopMarketData.find(mkt => mkt.md.includes(mktData.do));
    //   if (domainMatch) return domainMatch;
    // }

    // Priority 3: Region match
    if (mktData.re) {
      const regionMatches = this.shopMarketData.filter(mkt =>
        // Market region is defined since we check it
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        mkt.mr.includes(mktData.re),
      );

      // If exactly one region match, return it
      if (regionMatches.length === 1) {
        return regionMatches[0];
      }

      // Priority 4: If multiple region matches, try to narrow down by locale
      if (regionMatches.length > 1 && mktData.lo) {
        const localeMatch = regionMatches.find(mkt => mkt.ml === mktData.lo);
        if (localeMatch) return localeMatch;

        // If no locale match, return first region match as fallback
        return regionMatches[0];
      }
    }

    return undefined;
  }

  static isMarketDataEmpty(mktData: TMarketIdentifiers): boolean {
    return (
      mktData.mh === '' &&
      mktData.mid === '' &&
      mktData.re === '' &&
      mktData.lo === ''
    );
  }
}
